/**
 * Helpers for working with the AcademicYear descriptor codeValue format.
 *
 * Codes look like "2025-2026". The Pennsylvania school year runs roughly
 * July 1 through June 30, so we use those as the picker bounds. We do
 * NOT try to read these from the descriptor's effectiveBeginDate /
 * effectiveEndDate fields — those govern the descriptor's own validity,
 * not the year's date span.
 */

const YEAR_CODE_RE = /^(\d{4})-(\d{4})$/;

export interface AcademicYearBounds {
  /** Inclusive lower bound (July 1 of the start year). */
  fromDate: Date;
  /** Inclusive upper bound (June 30 of the end year). */
  toDate: Date;
  /** Numeric start year (e.g. 2025 for code "2025-2026"). */
  startYear: number;
  /** Numeric end year (e.g. 2026 for code "2025-2026"). */
  endYear: number;
}

export function parseAcademicYearCode(code: string): AcademicYearBounds | null {
  const match = YEAR_CODE_RE.exec(code);
  if (!match) return null;
  const startYear = Number(match[1]);
  const endYear = Number(match[2]);
  if (Number.isNaN(startYear) || Number.isNaN(endYear)) return null;
  if (endYear !== startYear + 1) return null;
  return {
    fromDate: new Date(startYear, 6, 1), // Jul 1 (months are 0-indexed)
    toDate: new Date(endYear, 5, 30), // Jun 30
    startYear,
    endYear,
  };
}

/** Compare an AcademicYear descriptor codeValue for "most recent first" sort. */
export function compareYearCodesDesc(a: string, b: string): number {
  const ay = YEAR_CODE_RE.exec(a)?.[1] ?? '';
  const by = YEAR_CODE_RE.exec(b)?.[1] ?? '';
  return by.localeCompare(ay);
}

/** Format a Date as YYYY-MM-DD using local time (no timezone shifts). */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse an ISO date string (YYYY-MM-DD) as a local-time Date. */
export function fromIsoDate(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/**
 * SpecialDayType codeValues for which weekend dates are allowed in the picker.
 *
 * Hard-coded here per the design decision documented in
 * docs/features/CALENDAR.md — keeping this rule out of the descriptor
 * row keeps the descriptor table feature-agnostic.
 */
export const WEEKEND_ALLOWED_SPECIAL_DAY_CODES: ReadonlySet<string> = new Set([
  'Graduation',
]);
